// チュートリアル：案内役のユズハが、初めての戦い（操作）と各画面の初訪問で要点だけを案内する。
// 魔王の力（妖力など）は、ユズハが「古い言い伝えで聞いた」という形で話す。
// 見終えた案内はサーバー（Profile.tutorial）に覚える。設定の「案内をもう一度見る」で最初から。
import { api, track } from '../api';
import type { BattleController } from '../battle/controller';
import type { BoardView } from '../battle/render';
import { onMounted } from '../router';
import { showLoginBonus } from '../ui/login-bonus';
import { state } from '../state';
import { closeCoach, coaching, quietCoach, say, type Line } from './coach';
import { PROLOGUE, PROLOGUE_STAGE, cellAt, dist, type BuildingType, type TargetRef } from '@hyakki/core';
import { sameRef } from '../battle/adapter';
import { firstBattleHint, FIRST_BATTLE_HINTS } from './first-battle';

const local = new Set<string>();
export const seen = (id: string) => local.has(id) || !!state.profile?.tutorial?.includes(id);
export function markSeen(...ids: string[]) {
  const fresh = ids.filter((id) => !seen(id));
  if (!fresh.length) return;
  for (const id of fresh) local.add(id);
  if (state.profile) state.profile.tutorial = [...(state.profile.tutorial ?? []), ...fresh];
  void api.tutorial(fresh).catch(() => undefined);
}
/** 設定の「案内をもう一度見る」 */
export async function resetTutorial() {
  local.clear();
  if (state.profile) state.profile.tutorial = [];
  await api.tutorial([], true);
}
/** 案内を飛ばした：そのまとまり（prefix）を全部見たことにする */
function skipAll(ids: string[]) {
  markSeen(...ids);
}

/** 1つの案内を、まだ見ていなくて、ほかの案内が出ていなければ出す。見終えたら（飛ばしても）印を付ける */
async function run(id: string, lines: Line[], group: string[] = [id]): Promise<boolean> {
  if (seen(id) || coaching()) return false;
  markSeen(id); // 途中で画面を離れても、同じ案内を何度も出さない
  const ok = await say(lines);
  if (!ok) skipAll(group);
  return ok;
}

// ---------- 画面の初訪問 ----------
const SCREEN: Record<string, { id: string; when?: () => boolean; lines: Line[] }> = {
  top: {
    id: 'top',
    lines: [
      { face: 'happy', text: 'ここが里の拠点です。困ったら私に聞いてください。' },
      { face: 'point', target: '[data-act="sortie"]', text: '戦に出るときは「出撃」から。まずは一戦、行きましょう。' },
    ],
  },
  stages: {
    id: 'map',
    lines: [
      { face: 'point', text: 'まずは潮見の戦場を押さえて、潮見砦へ。その先の青葉地方に、敵の本城があります。' },
      { face: 'normal', target: '.sortie-btn', text: '戦場を選んだら「出陣」。兵糧を使って出撃します。' },
    ],
  },
  chara: {
    id: 'chara',
    when: () => (state.profile?.items.length ?? 0) > 4,
    lines: [
      { face: 'point', target: '.ch-tabs [data-tab="equip"]', text: '拾った装備やお守りは「装備」で付けられます。枠を押して選び、迷ったら「おすすめ装備」を。いちばん強い組み合わせにしてくれます。' },
      { face: 'normal', target: '.ch-tabs', text: '「装備」には戦に持っていく道具もまとめてあります。「能力値」で術や妖力の中身を、「スキル」で技と術を選べます。' },
    ],
  },
  enhance: {
    id: 'enhance',
    lines: [{ face: 'point', text: '強化では装備・お守り・式神・建物図面を育てます。式神は霊玉で式神Lvが上がり、Lv15・30で進化します。' }],
  },
  village: {
    id: 'village',
    lines: [{ face: 'happy', text: '私たちの里です。施設を建てておくと、銭や材木が少しずつ貯まります。ときどき受け取りに来てください。' }],
  },
  gacha: {
    id: 'gacha',
    lines: [{ face: 'point', text: 'ガチャ券でおまもりガチャを引くと、お守りが手に入ります。' }],
  },
  result: {
    id: 'result',
    when: () => (state.progress?.cleared.length ?? 0) >= 1,
    lines: [
      { face: 'happy', text: '勝ちましたね。勝つと主EXP・銭・装備が手に入ります。' },
      { face: 'point', target: '[data-nav="chara"]', text: '拾った装備は「編成」で付けられます。次の戦の前に、どうぞ。' },
    ],
  },
};

// ---------- 画面の中のタブの初訪問（強化：装備・お守り・式神） ----------
const TAB: Record<string, { id: string; lines: Line[] }> = {
  'enhance:equip': {
    id: 'enh.equip',
    lines: [
      { face: 'point', text: '装備は銭で強化できます。装備を選んで「強化」。ただし、強化には上限があります。' },
      { face: 'normal', text: '上限まで強化したら「限界突破」。同じ装備（レア度が同じか上のもの）を1つ素材にすると、上限が10上がります。里の鍛冶屋を育てると、もっと突破できます。' },
    ],
  },
  'enhance:omamori': {
    id: 'enh.omamori',
    lines: [
      { face: 'point', target: '[data-a="fusemode"]', text: '「錬成」は、要らないお守り3つと銭で、新しいお守りを1つ作ります。' },
      { face: 'normal', text: 'できるお守りのレア度は素材の平均くらい。運がよければレア度が1〜2つ上がります。素材は消えるので、残したいお守りはロックしておきましょう。' },
    ],
  },
  'enhance:shiki': {
    id: 'enh.shiki',
    lines: [
      { face: 'point', text: '式神は、戦場で倒した妖が仲間になったものです。式神になる妖は10種族。戦場の情報の「式神の候補」に載っています。' },
      { face: 'surprised', text: 'ただ、めったに仲間になりません。気長に戦い続けましょう。' },
      { face: 'happy', text: '式神は持っているだけで4人全員が強くなります。霊玉で式神Lvを上げると★2・★3へ進化。キャラごとに1体を式神召喚にセットすると、戦いで技を使って助けてくれます。' },
    ],
  },
};

/** タブを開いた：そのタブの案内がまだなら出す（画面の案内やまとめ演出が閉じてから。別のタブ・画面に移ったら出さない） */
export function tabTutorial(screen: string, tab: string, stillOn: () => boolean) {
  const s = TAB[`${screen}:${tab}`];
  if (!s || !state.profile || seen(s.id)) return;
  const at = location.hash;
  let tries = 0;
  const tick = () => {
    if (location.hash !== at || !stillOn() || seen(s.id)) return;
    if (document.querySelector('.modal-wrap, .hf') || coaching() || justTouched()) {
      if (++tries < 120) setTimeout(tick, 500);
      return;
    }
    // 強化のタブの案内を見たら、強化の画面の案内（強化では装備・お守り…）はもう出さない（あとから重ねて出て、何度も出ているように見えた）
    if (screen === 'enhance') markSeen('enhance');
    void run(s.id, s.lines);
  };
  setTimeout(tick, 400);
}

// 押した直後には案内を出さない（出たばかりの覆いに、押そうとしていた次の操作が吸われる）
let touchedAt = 0;
if (typeof window !== 'undefined') window.addEventListener('pointerdown', () => (touchedAt = performance.now()), { capture: true, passive: true });
const justTouched = () => performance.now() - touchedAt < 900;

let installed = false;
/** 画面の初訪問の案内を付ける（main から1回） */
export function installTutorial() {
  if (installed) return;
  installed = true;
  onMounted((name) => {
    closeCoach();
    // 初回の流れではログインボーナスを初戦の前に出さないので、TOP を開いたときに出す（受け取り済みなら何もしない）
    if (name === 'top' && state.me && !state.me.loginBonus?.claimedToday) void showLoginBonus();
    const s = SCREEN[name];
    if (!s || !state.profile || seen(s.id) || (s.when && !s.when())) return;
    // 画面の絵・ボタンが並ぶのを待ち、ログインボーナスなどの札・周回明けのまとめ演出（.hf）が閉じてから（その画面にいる間だけ）
    const at = location.hash;
    let tries = 0;
    const tick = () => {
      if (location.hash !== at || seen(s.id)) return;
      if (document.querySelector('.modal-wrap, .hf') || coaching() || justTouched()) {
        if (++tries < 120) setTimeout(tick, 500);
        return;
      }
      void run(s.id, s.lines);
    };
    setTimeout(tick, 400);
  });
}

// ---------- 前世は基本操作、現代の初戦は状況に応じた短い補足 ----------

/** 戦闘画面に付ける。プロローグの戦いなら一本道の案内を出す。戻り値で外す */
export function battleTutor(ctl: BattleController, view: BoardView, stageId: number): () => void {
  const first = stageId === 1 && !(state.profile?.progress['1']?.clears);
  if (stageId !== PROLOGUE_STAGE && !first) return seen('battle.ward') ? () => undefined : wardTutor(ctl, view);
  const off = stageId === PROLOGUE_STAGE ? prologueTutor(ctl, view) : presentTutor(ctl, view);
  // 演出中（行動の再生・相手の手番）は案内を隠して、攻撃や奥義の場面を暗くしない
  const quiet = setInterval(() => quietCoach(ctl.busy || (!ctl.ui && !ctl.over)), 50);
  return () => {
    clearInterval(quiet);
    quietCoach(false);
    off();
  };
}

/** 初めて結界（番人のいる敵本陣）のある戦い：自分の最初の手番に、ユズハが結界と番人の関係を話す */
function wardTutor(ctl: BattleController, view: BoardView): () => void {
  /** マスを囲む枠。w・h はマスの大きさ（view.s）に対する幅・高さ、up は中心から上へずらす量（城は結界の半球まで入れる） */
  const cellBox = (cell: () => number | null | undefined, w = 2.6, h = 3.1, up = 0.65) => () => {
    const c = cell();
    if (c == null || c < 0) return null;
    const p = view.center(c);
    const r = view.cv.getBoundingClientRect();
    const s = view.s;
    return new DOMRect(r.left + p.x - (s * w) / 2, r.top + p.y - s * up - (s * h) / 2, s * w, s * h);
  };
  const guard = () => ctl.board.units.find((u) => u.kind === 'boss' && u.side === 'red' && u.alive)?.cell;
  const timer = setInterval(() => {
    if (!ctl.ui || ctl.auto || ctl.busy || ctl.over || coaching() || document.querySelector('.modal-wrap')) return;
    if (ctl.ui.phase !== 'start' || ctl.ui.target || ctl.ui.build) return;
    if (!ctl.board.guardian || guard() == null) return;
    clearInterval(timer);
    void run('battle.ward', [
      { face: 'surprised', target: cellBox(() => ctl.board.cores.red.cell, 4.2, 4.0, 0.35), text: '城が結界に守られているみたい！　このままだと、城へのダメージが減っちゃう。' },
      { face: 'point', target: cellBox(guard), text: '結界の力は、城の前にいる妖怪から送られているはず。アイツを倒せば結界が破れるよ！' },
    ]);
  }, 300);
  return () => {
    clearInterval(timer);
    closeCoach();
  };
}

/** 現代の初戦：自分の手番だけで案内し、操作対象は固定しない。再開後も既読を引き継ぐ。 */
function presentTutor(ctl: BattleController, view: BoardView): () => void {
  const timer = setInterval(() => {
    if (!ctl.ui || ctl.auto || ctl.busy || ctl.over || coaching() || document.querySelector('.modal-wrap')) return;
    // 自分で選んでいる途中（経路・攻撃・建築）には割り込まない。
    if (ctl.ui.target || ctl.ui.build || ctl.ui.phase === 'rolled') return;
    const hint = firstBattleHint(ctl.board, ctl.commands.some(c => c.t === 'move'), seen);
    if (!hint) return;
    const target = hint.cell != null ? () => {
      const p = view.center(hint.cell!);
      const r = view.cv.getBoundingClientRect();
      const s = view.s * 1.3;
      return new DOMRect(r.left + p.x - s, r.top + p.y - s * 1.5, s * 2, s * 2.4);
    } : hint.target;
    if (hint.id === 'battle.relay-captured') markSeen('battle.relay');
    void run(hint.id, [{ text: hint.text, target }], [...FIRST_BATTLE_HINTS]);
  }, 300);
  return () => { clearInterval(timer); closeCoach(); };
}

// ---------- プロローグ（三百年前・天下統一の最終戦。酒呑童子を操作し、主人公＝「あなた」が短く答える） ----------

/**
 * 3手番の一本道（core の PROLOGUE）：城への道の足軽 → 幟を立てて侍大将 → 紅蓮斬で落城。教えるのは攻撃・幟・術だけ（陣地・龍脈は現代の初戦）。
 * 毎手番に倒す相手がいて、手番終了を待たない。止まるマスは PROLOGUE.stand。操作を待つ間だけ対象を絞る。
 */
type Role = 'ashigaru' | 'general';
function prologueTutor(ctl: BattleController, view: BoardView): () => void {
  const my = () => !!ctl.ui && !ctl.auto && !ctl.busy && !ctl.over;
  const act = (cmd: string) => `.actions [data-cmd="${cmd}"]`;
  const turn = () => ctl.board.turn;
  const phase = () => ctl.ui?.phase;
  const hero = () => ctl.board.units.find((u) => u.kind === 'hero' && u.side === 'blue');
  const at = () => hero()?.cell ?? 0;
  const core = () => ctl.board.cores.red.cell;
  // 案内で倒す相手（置いたマスで見分ける）
  const ids: Partial<Record<Role, number>> = {};
  const foe = (role: Role) => {
    if (ids[role] == null) {
      const f = PROLOGUE.foes.find((x) => x.role === role)!;
      const c = cellAt(...f.at);
      ids[role] = ctl.board.units.find((u) => u.kind === 'neutral' && u.cell === c)?.id ?? -1;
    }
    return ctl.board.units.find((u) => u.id === ids[role] && u.alive && u.cell != null);
  };
  const stage = (): Role | 'core' => (foe('ashigaru') ? 'ashigaru' : foe('general') ? 'general' : 'core');
  const built = (t: BuildingType) => ctl.board.units.some((u) => u.kind === 'building' && u.side === 'blue' && u.alive && u.btype === t);
  const nBuilt = () => ctl.board.units.filter((u) => u.kind === 'building' && u.side === 'blue' && u.alive).length;
  /** 案内するマスを囲む光の枠。 */
  const cellRect = (cell: () => number | null | undefined) => () => {
    const c = cell();
    if (c == null) return null;
    const p = view.center(c);
    const r = view.cv.getBoundingClientRect();
    const s = view.s * 1.3;
    return new DOMRect(r.left + p.x - s, r.top + p.y - s * 1.25, s * 2, s * 2.1);
  };
  /** 賽の後の行き先：決めたマス（届かなければ相手の隣で一番近いマス） */
  const standFor = (role: Role) => {
    const moves = [...(ctl.ui?.legal.moves ?? [])];
    const c = cellAt(...PROLOGUE.stand[role]);
    if (c != null && moves.includes(c)) return c;
    const goal = foe(role)?.cell;
    return goal == null ? undefined : moves.filter((m) => dist(m, goal) <= 1).sort((a, b) => dist(a, core()) - dist(b, core()) || a - b)[0];
  };
  /** 建てるマス：酒呑の隣で、城に近いマス */
  const buildCell = (t: BuildingType) =>
    [...(ctl.ui?.legal.builds.get(t) ?? [])].filter((c) => dist(c, at()) <= 1).sort((a, b) => dist(a, core()) - dist(b, core()) || a - b)[0];

  // 一本道の絞り込み（行き先のマス・狙う相手・建てる物）。案内が操作を待っている間だけかける。外れた遊び方をしても詰まらないよう、案内の無い間は自由
  type Aim = { move?: () => number | undefined; target?: () => TargetRef | null; mode?: 'attack' | 'skill'; build?: BuildingType } | null;
  let aim: Aim = null;
  const lockTick = () => {
    const before = JSON.stringify(ctl.lock);
    setLock();
    // 光らせるマスが変わったら描き直す（決めたマスだけ光る）
    if (JSON.stringify(ctl.lock) !== before) ctl.render();
  };
  const setLock = () => {
    if (!aim || !coaching() || !ctl.ui) {
      ctl.lock = null;
      return;
    }
    if (aim.move) {
      const c = ctl.ui.phase === 'rolled' ? aim.move() : undefined;
      ctl.lock = { cells: c != null ? [c] : [] };
    } else if (aim.build) {
      const c = buildCell(aim.build);
      ctl.lock = { cells: c != null ? [c] : [] };
    } else if (aim.target) {
      const t = aim.target();
      ctl.lock = t ? { target: t, ...(aim.mode ? { mode: aim.mode } : {}) } : null;
    } else ctl.lock = { cells: [] };
  };
  const moveCell = () => (ctl.lock?.cells?.[0] ?? null);
  /** 六角のパレット全体（ボタンは枠の外に並ぶので、出ているボタンを囲む） */
  const paletteRect = () => {
    const rs = [...document.querySelectorAll<HTMLElement>('.actions.palette > .act:not([hidden])')].map((b) => b.getBoundingClientRect()).filter((r) => r.width > 0);
    if (!rs.length) return null;
    const x = Math.min(...rs.map((r) => r.left));
    const y = Math.min(...rs.map((r) => r.top));
    return new DOMRect(x, y, Math.max(...rs.map((r) => r.right)) - x, Math.max(...rs.map((r) => r.bottom)) - y);
  };
  const alive = (id: number) => ctl.board.units.some((u) => u.id === id && u.alive);

  /** 賽を振って光るマスへ（この手番のうち）。text＝賽の案内。初めての賽のあとだけ役の説明 */
  const rollMove = (roll: string, move: string, t: number): Line[] => [
    { who: 'system', target: act('roll'), text: roll, wait: () => phase() !== 'start' || turn() > t, hint: '「賽」を押す' },
    ...once('role', [{ who: 'system', text: 'ゾロ目などの「役」が出ると、その手番の攻撃が強くなります。' }]),
    moveLine(move, t),
  ];
  const moveLine = (move: string, t: number): Line => ({ who: 'system', target: cellRect(moveCell), text: move, wait: () => phase() === 'moved' || turn() > t, hint: '光るマスを2回押して移動', allow: [() => view.cv, act('move'), act('stay'), act('cancel')] });
  /** 建築：「建築」→ 種類 → 光るマス */
  const buildLines = (t: BuildingType, desc: string): Line[] => [
    { who: 'system', target: '.actions [data-act="buildmenu"]', text: '「建築」で陣地に建物を建てられます。', wait: () => !!document.querySelector('.build-menu') || built(t), hint: '「建築」を押す', allow: [] },
    { who: 'system', target: `.build-opt[data-t="${t}"]`, text: desc, wait: () => ctl.ui?.build === t || built(t), hint: '「幟」を押す', allow: [] },
    { who: 'system', target: cellRect(() => ctl.lock?.cells?.[0]), text: '光るマスを押して建てましょう。建てても手番は終わりません。', wait: () => built(t), hint: '光るマスを押す', allow: [() => view.cv] },
  ];
  /** 攻撃：相手を押して構え → もう一度押す（初回だけ予測ダメージも説明） */
  const attackLines = (id: number, cell: () => number | null | undefined, name: string, t: number, first: boolean): Line[] => {
    const ref: TargetRef = { kind: 'actor', id };
    const aimed = () => sameRef(ctl.ui?.target, ref) && ctl.ui?.mode === 'attack';
    return first
      ? [
          { who: 'system', target: cellRect(cell), text: `敵を押すと「攻撃」の構えになります。${name}を押しましょう。`, wait: () => aimed() || !alive(id) || turn() > t, hint: `${name}を押す`, allow: [() => view.cv, act('attack')] },
          { who: 'system', target: '.pal-sim', text: '相手のHPがどれだけ減るかが出ます。ゲージが金に光れば、この一撃で倒せます。' },
          { who: 'system', target: act('attack'), text: '「攻撃する」を押すと攻撃します。', wait: () => !alive(id) || turn() > t, hint: '「攻撃する」を押す', allow: [() => view.cv, act('attack'), act('cancel')] },
        ]
      : [{ who: 'system', target: cellRect(cell), text: `${name}を押して攻撃しましょう。`, wait: () => !alive(id) || turn() > t, hint: `${name}を押す`, allow: [() => view.cv, act('attack'), act('cancel')] }];
  };
  /** 届かないとき：手番終了（詰まらないように） */
  const endTurn = (text: string, t: number): Line[] => [{ who: 'system', target: act('wait'), text, wait: () => turn() > t || !!ctl.over, hint: '「手番終了」を押す', allow: [() => view.cv, act('move'), act('stay'), act('cancel')] }];

  // 物語の台詞・説明は1回だけ。案内は「今の盤の様子」から毎回決める（手番×段階×建物の数ごとに1回）
  const said = new Set<string>();
  const once = (id: string, lines: Line[]) => (said.has(id) ? [] : (said.add(id), lines));
  const NAME: Record<Role, string> = { ashigaru: '足軽', general: '侍大将' };
  /**
   * 賽を振った後・動く前（セーブから戻ったときなど）：移動の案内。手番終了のボタンはまだ無いので、手番終了を案内すると詰まる。
   * 決めたマスに届かなければ絞らない（好きなマスへ動くか「動かない」）
   */
  const rolled = (to: () => number | undefined, move: string, t: number): { aim: Aim; lines: Line[] } =>
    to() != null
      ? { aim: { move: to }, lines: [moveLine(move, t)] }
      : { aim: null, lines: [{ who: 'system', text: '光るマスを2回押して移動。動かないなら「動かない」。', wait: () => phase() === 'moved' || turn() > t, hint: '光るマスを2回押す', allow: [() => view.cv, act('move'), act('stay'), act('cancel')] }] };
  const plan = (): { aim: Aim; lines: Line[] } => {
    const t = turn();
    const st = stage();
    // 離脱の計測：足軽・侍大将を倒したところ
    if (st !== 'ashigaru') track('prologue.ashigaru');
    if (st === 'core') track('prologue.general');
    const coreRef: TargetRef = { kind: 'core', side: 'red' };
    if (st === 'core') {
      // 城の隣にいれば、賽を振らずに術で落とす
      const ui = ctl.ui!;
      const canSkill = ui.legal.skills.some((x) => sameRef(x, coreRef));
      if (canSkill || ui.legal.attacks.some((x) => sameRef(x, coreRef)))
        return {
          aim: { target: () => coreRef, mode: canSkill ? 'skill' : 'attack' },
          lines: [
            ...once('jutsu', [
              { who: 'shuten', text: '残るは城だけだ。紅蓮斬でぶち抜くぞ！' },
              { who: 'system', target: act('skill'), text: '「術」はキャラごとの特別な技です。幟で強めた酒呑の術で、城を落としましょう。' },
              { who: 'system', text: '術を放つときも賽を3つ振り、目が大きいほど威力が上がります。一度使うと、再び使えるまで待ち手番が必要です。' },
            ]),
            { who: 'system', target: act(canSkill ? 'skill' : 'attack'), text: '敵の本陣を落とせば勝利です。', wait: () => !!ctl.over || turn() > t, hint: canSkill ? '「術」を押して、城を押す' : '「攻撃」を押して、城を押す', allow: [() => view.cv, act('cancel')] },
          ],
        };
      const toCore = () => [...(ctl.ui?.legal.moves ?? [])].filter((c) => dist(c, core()) <= 1).sort((a, b) => a - b)[0];
      if (phase() === 'start') return { aim: { move: toCore }, lines: rollMove('賽を振って、城の前へ。', '光るマスを2回押して移動。', t) };
      if (phase() === 'rolled') return rolled(toCore, '城の前へ。光るマスを2回押して移動。', t);
      return { aim: null, lines: endTurn('まだ城に届きません。「手番終了」で次の手番へ。', t) };
    }
    const f = foe(st)!;
    if (phase() === 'rolled') return rolled(() => standFor(st), st === 'ashigaru' ? '足軽の前へ。光るマスを2回押すと移動します。' : '光るマスを2回押して移動。', t);
    if (phase() === 'start') {
      const lines: Line[] =
        st === 'ashigaru'
          ? [
              ...once('open', [
                { who: 'shuten', text: '最後の城だな、大将！' },
                { who: 'you', text: 'あぁ。' },
                { who: 'shuten', text: '蹴散らすぞ！' },
              ]),
              { who: 'system', target: paletteRect, text: '行動は盤の上のパレットから選びます。マスを押すと、そこにパレットが開きます。' },
              ...rollMove('真ん中の「賽」で賽を3つ振ります。合計が大きいほど遠くへ進めます。', '足軽の前へ。光るマスを2回押すと移動します。', t),
            ]
          : rollMove('賽を振って、侍大将と城の前へ。', '光るマスを2回押して移動。', t);
      return { aim: { move: () => standFor(st) }, lines };
    }
    // 移動のあと
    const ui = ctl.ui!;
    const ref: TargetRef = { kind: 'actor', id: f.id };
    if (!ui.legal.attacks.some((x) => sameRef(x, ref))) return { aim: null, lines: endTurn(`まだ${NAME[st]}に届きません。「手番終了」で次の手番へ。`, t) };
    if (st === 'general' && !built('nobori') && buildCell('nobori') != null)
      return {
        aim: { build: 'nobori' },
        lines: [
          ...once('flag', [{ who: 'shuten', text: '侍大将だ。旗を立てろ！' }]),
          ...buildLines('nobori', '「幟」の隣のマスにいると、攻撃が強くなります。'),
        ],
      };
    const lines: Line[] = [];
    lines.push(...attackLines(f.id, () => (alive(f.id) ? f.cell : null), NAME[st], t, st === 'ashigaru'));
    return { aim: { target: () => (alive(f.id) ? ref : null), mode: 'attack' }, lines };
  };

  let on = true;
  const handled = new Set<string>();
  const timer = setInterval(() => {
    if (!on) return;
    lockTick();
    if (!my() || coaching() || document.querySelector('.modal-wrap')) return;
    const key = `${turn()}:${phase()}:${nBuilt()}:${stage()}:${ctl.armed}`;
    if (handled.has(key)) return;
    handled.add(key);
    const p = plan();
    aim = p.aim;
    void say(p.lines).then((ok) => {
      // 「案内を飛ばす」：以後は自由に遊ぶ（絞り込みもやめる）
      if (!ok && on) {
        on = false;
        aim = null;
        ctl.lock = null;
      }
    });
  }, 200);
  return () => {
    on = false;
    clearInterval(timer);
    ctl.lock = null;
    closeCoach();
  };
}
